import axiosInstance from '@/libs/axios_config.ts'
import { z } from 'zod'
import { parseRequestError } from '@/common/utilities.ts'
import { RequestResponse } from '@/common/types_and_interfaces.ts'
import { NoContent } from '@/app/common/types_and_interfaces'
import { LOG_TYPES } from '@/app/pages/family_application/constants'

export const UserEndpoints = {
  login: 'access/scholars/login',
  refresh: '/scholar/auth/refresh',
  register: '/applicants/public_registration',
  complete_registration_scholar:
    '/scholar/applicants/:applicant_id/complete_registration',
  complete_registrations:
    '/admin/applicants/:applicant_id/complete_registration',
  reset_password_email: '/scholar/applicants/send_reset_password_email',
  reset_password: '/scholar/applicants/:applicant_id/reset_password',
  resend_2fa: '/scholar/auth/resend-2fa',
  verify_2fa: '/scholar/auth/verify-2fa',
  email_verification: '/scholar/applicants/:user_id/email_verification',
  resend_email_verification: '/scholar/applicants/resend_email_verification',
  change_password: '/scholar/applicants/:user_id/change_password',
  permissions: '/permissions',
} as const

type RegisterUserReq = {
  email: string
  password: string
  program_id: number
  user_has_accepted_text_notifications: boolean
  phone?: string
}

const RegisterUserRespSchema = z.object({
  id: z.number(),
})

export type UserRegistrationResp = z.infer<typeof RegisterUserRespSchema>

export const RegisterUser = async (
  requestBody: RegisterUserReq
): Promise<RequestResponse<UserRegistrationResp>> => {
  try {
    const response = await axiosInstance.post(
      UserEndpoints.register,
      requestBody
    )
    const cleanResponse = RegisterUserRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

const AuthResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  message: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  custom_expiration_time: z.number().nullable().optional(),
})

export type AuthResponse = z.infer<typeof AuthResponseSchema>

export const AuthenticateUser = async (
  email: string,
  password: string,
  remember_me: boolean
): Promise<RequestResponse<AuthResponse>> => {
  try {
    const response = await axiosInstance.post(UserEndpoints.login, {
      email,
      password,
      remember_me,
    })
    const cleanResponse = AuthResponseSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export const RefreshToken = async (): Promise<
  RequestResponse<AuthResponse>
> => {
  try {
    const response = await axiosInstance.post(UserEndpoints.refresh, {})
    const cleanResponse = AuthResponseSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

const CompleteUserRegistrationResponseSchema = z.object({
  id: z.number(),
})
type CompleteUserRegistrationResponse = z.infer<
  typeof CompleteUserRegistrationResponseSchema
>
export type CompleteRegistrationProps = {
  email: string
  password: string
  token: string
  applicant_id: number
  phone?: string
  fromAdmin?: boolean
  user_has_accepted_text_notifications: boolean
}
export const CompleteUserRegistration = async ({
  email,
  password,
  token,
  applicant_id,
  fromAdmin = true,
  phone,
  user_has_accepted_text_notifications,
}: CompleteRegistrationProps): Promise<
  RequestResponse<CompleteUserRegistrationResponse>
> => {
  try {
    const endpoint = fromAdmin
      ? UserEndpoints.complete_registrations.replace(
          ':applicant_id',
          applicant_id.toString()
        )
      : UserEndpoints.complete_registration_scholar.replace(
          ':applicant_id',
          applicant_id.toString()
        )
    const payload = {
      email,
      password,
      phone: phone?.replace(/\D/g, '') ?? null,
      user_has_accepted_text_notifications,
    }
    const response = await axiosInstance.put(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-From-Admin': fromAdmin ? 'true' : 'false',
      },
    })
    return {
      data: CompleteUserRegistrationResponseSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}
// For Reset Passwod Email

const ResetPasswordEmailSchema = z.object({
  message: z.string(),
})
export type ResetPasswordEmailResponse = z.infer<
  typeof ResetPasswordEmailSchema
>

export const SendResetPasswordEmail = async (
  email: string
): Promise<RequestResponse<ResetPasswordEmailResponse>> => {
  try {
    const response = await axiosInstance.post(
      UserEndpoints.reset_password_email,
      {
        email,
      }
    )
    const cleanResponse = ResetPasswordEmailSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// RESET PASSWORD

type ResetPasswordRequest = {
  token: string
  user_id: string
  payload: {
    password: string
  }
}

const ResetPasswordResponseSchema = z.object({
  message: z.string().nullable(),
})

type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>

export const ResetPassword = async (
  requestBody: ResetPasswordRequest
): Promise<RequestResponse<ResetPasswordResponse>> => {
  try {
    const endpoint = UserEndpoints.reset_password.replace(
      ':applicant_id',
      requestBody.user_id
    )
    const response = await axiosInstance.put(endpoint, requestBody.payload, {
      headers: {
        Authorization: `Bearer ${requestBody.token}`,
      },
    })
    return {
      data: ResetPasswordResponseSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

// For Resend TWO FA

const ResendTwoFaSchema = z.object({
  message: z.string(),
})
export type ResendTWOFAResponse = z.infer<typeof ResendTwoFaSchema>

export const ResendTwoFA = async (
  phone: string
): Promise<RequestResponse<ResendTWOFAResponse>> => {
  try {
    const response = await axiosInstance.post(UserEndpoints.resend_2fa, {
      phone,
    })
    const cleanResponse = ResendTwoFaSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// For Verify TWO FA Code

export type VerifyTwoFaResponse = z.infer<typeof AuthResponseSchema>

export const VerifyTwoFA = async (
  phone: string,
  code: string
): Promise<RequestResponse<VerifyTwoFaResponse>> => {
  try {
    const response = await axiosInstance.post(UserEndpoints.verify_2fa, {
      phone,
      code,
    })
    const cleanResponse = AuthResponseSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// For Email Verification

const EmailVerificationRespSchema = z.object({
  id: z.number(),
})

export type EmailVerificationResponse = z.infer<
  typeof EmailVerificationRespSchema
>

export const VerifyEmail = async (
  user_id: string,
  token: string
): Promise<RequestResponse<EmailVerificationResponse>> => {
  try {
    const endpoint = UserEndpoints.email_verification.replace(
      ':user_id',
      user_id
    )
    const response = await axiosInstance.put(
      endpoint,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return {
      data: EmailVerificationRespSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

// For Reset Passwod Email

const ResendVerificationEmailSchema = z.object({
  message: z.string(),
})
export type ResendVerificationEmailResponse = z.infer<
  typeof ResendVerificationEmailSchema
>

export const ResendVerificationEmail = async (
  email: string
): Promise<RequestResponse<ResendVerificationEmailResponse>> => {
  try {
    const response = await axiosInstance.put(
      UserEndpoints.resend_email_verification,
      {
        email,
      }
    )
    const cleanResponse = ResendVerificationEmailSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type LogUserActivityRequest = {
  log_type: (typeof LOG_TYPES)[keyof typeof LOG_TYPES]
  suffix_text: string
  targetUserName?: string
}

export async function LogUserActivity({
  log_type,
  suffix_text,
  targetUserName,
}: LogUserActivityRequest): Promise<RequestResponse<NoContent>> {
  const endpoint = `/log_user_activity`
  const params = new URLSearchParams({
    log_type,
    suffix_text,
  })
  if (targetUserName) params.append('targetUserName', targetUserName)
  try {
    await axiosInstance.post(`${endpoint}?${params}`)
    return { data: '', error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export async function GETInvitationStatus(
  email: string,
  invitationToken: string
): Promise<RequestResponse<NoContent>> {
  try {
    await axiosInstance.get(
      `/public/applicant/invitation_status?email=${email}&invitation_token=${invitationToken}`
    )
    return { data: '', error: null }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

export type ResendInvitationRequest = {
  email: string
  invitation_token: string
  call_back_url: string
  program_id: number
}
export async function POSTResendInvitation(
  payload: ResendInvitationRequest
): Promise<RequestResponse<NoContent>> {
  try {
    await axiosInstance.post(
      `/public/applicant/resend_email_invitation`,
      payload
    )
    return { data: '', error: null }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

// Change PASSWORD

type ChangePasswordRequest = {
  user_id: number
  payload: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }
}

const ChangePasswordResponseSchema = z.object({
  id: z.number(),
})

type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>

export const ChangePassword = async (
  requestBody: ChangePasswordRequest
): Promise<RequestResponse<ChangePasswordResponse>> => {
  try {
    const response = await axiosInstance.put(
      UserEndpoints.change_password.replace(
        ':user_id',
        requestBody.user_id.toString()
      ),
      requestBody.payload
    )
    return {
      data: ChangePasswordResponseSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

export async function GETApplicantPermissions(
  program_id: number
): Promise<RequestResponse<{ has_access: boolean }>> {
  try {
    const response = await axiosInstance.get(UserEndpoints.permissions, {
      params: { program_id },
    })
    return {
      data: z.object({ has_access: z.boolean() }).parse(response.data),
      error: null,
    }
  } catch (e) {
    return { error: parseRequestError(e), data: null }
  }
}
